import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import {
  FEE_CATEGORIES, budgetLine, homeFeeLabel, homeNote, listed, livingGap, regionLine, tuitionFor, yearlyTotal,
} from "@/lib/compareFacts";

const FEE_KEY = "wua:compare:fee-category";
const TONE = { ok: "text-emerald-800", soft: "text-amber-800", hard: "text-rose-800", missing: "text-stone-500 italic" };

const Missing = ({ children, testId }) => <span className="italic text-stone-500" data-testid={testId}>{children}</span>;

function Row({ title, names, cells, note, testId }) {
  return (
    <div className="min-w-0 border-t border-stone-200 pt-2" data-testid={testId}>
      <div className="text-[11px] font-semibold text-stone-700">{title}</div>
      <div className="mt-1 grid grid-cols-1 gap-2 md:grid-cols-2">
        {cells.map((c, i) => (
          <div key={i} className="min-w-0 break-words [overflow-wrap:anywhere] text-[12px] text-stone-700" data-testid={testId + "-" + (i ? "b" : "a")}>
            <div className="text-[10px] font-underground-sc tracking-wider text-stone-500 md:hidden">{names[i]}</div>
            {c}
          </div>
        ))}
      </div>
      {note && <p className="mt-1 text-[11px] text-stone-600" data-testid={testId + "-note"}>{note}</p>}
    </div>
  );
}

function Money({ m, per, perYear }) {
  const { format: f, currency } = useCurrency();
  const format = (v) => f(v, { decimals: 0 });
  return (
    <>
      About {format(m.usd)}{per}
      {perYear && <span className="text-stone-500"> (≈ {format(m.usd * 12)}/year)</span>}
      {m.currency !== currency && <span className="block text-[11px] text-stone-500">Listed as {listed(m, per)}</span>}
    </>
  );
}

function CourseCell({ o, i }) {
  const c = o.course;
  if (!c) return <Missing testId={"compare-course-missing-" + i}>Saved as a university only, so there are no course details.</Missing>;
  return (
    <>
      <span className="font-medium">{c.title || "Course title not listed"}</span>
      <span className="block text-[11px] text-stone-500">
        Catalogue subject: {c.catalogue_subject || "not listed"}
        {c.title_theme && c.catalogue_subject && !c.catalogue_subject.toLowerCase().includes(c.title_theme) && <> · the title itself is {c.title_theme}</>}
      </span>
    </>
  );
}

function LivingCell({ o, budget, i }) {
  const { format: f } = useCurrency();
  const format = (v) => f(v, { decimals: 0 });
  if (!o.living) return <Missing testId={"compare-living-missing-" + i}>No living-cost estimate for {o.city_name || "this city"} yet.</Missing>;
  return (
    <>
      <Money m={o.living} per="/month" perYear />
      <span className="block text-[11px] text-stone-500">Estimate for {o.city_name}{o.living.as_of ? " · " + o.living.as_of : ""}</span>
      <span className="block text-[11px] text-stone-700" data-testid={"compare-living-budget-" + i}>{budgetLine(o.living_vs_budget_usd, budget, format)}</span>
    </>
  );
}

function FeeLine({ label, m, year, i, k }) {
  return (
    <span className="block" data-testid={"compare-tuition-" + k + "-" + i}>
      {label}: {m ? <><Money m={m} per="/year" />{year && <span className="text-[11px] text-stone-500"> ({year})</span>}</> : <Missing>not listed</Missing>}
    </span>
  );
}

function TuitionCell({ o, category, i }) {
  const t = o.tuition;
  if (!t) return <Missing testId={"compare-tuition-missing-" + i}>No course selected, so no tuition fee.</Missing>;
  const home = <FeeLine label={homeFeeLabel(o.country)} m={t.home} year={t.year} i={i} k="home" />;
  const intl = <FeeLine label="International fee" m={t.international} year={t.year} i={i} k="international" />;
  if (category === "home") return home;
  if (category === "international") return intl;
  return <>{home}{intl}</>;
}

function TotalCell({ o, category, i }) {
  const { format: f } = useCurrency();
  const format = (v) => f(v, { decimals: 0 });
  const t = yearlyTotal(o, category);
  if (t.missing) return <Missing testId={"compare-total-missing-" + i}>No total: missing {t.missing.join(" and ")}.</Missing>;
  return <span data-testid={"compare-total-" + i}>About {format(t.usd)}/year (estimate: tuition + 12 months' living)</span>;
}

function FeePicker({ value, onChange }) {
  return (
    <fieldset className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-stone-700" data-testid="compare-fee-category">
      <legend className="sr-only">Which tuition fee category applies to you?</legend>
      <span className="text-stone-500">Show fees for:</span>
      {FEE_CATEGORIES.map((c) => (
        <label key={c.key} className="inline-flex cursor-pointer items-center gap-1">
          <input type="radio" name="compare-fee-category" value={c.key} checked={value === c.key} onChange={() => onChange(c.key)}
            className="h-3.5 w-3.5 accent-[#4C1D95]" data-testid={"compare-fee-category-" + c.key} />
          {c.label}
        </label>
      ))}
    </fieldset>
  );
}

export default function CompareFacts({ keys, names, nonce }) {
  const { format: f } = useCurrency();
  const format = (v) => f(v, { decimals: 0 });
  const [state, setState] = useState({ loading: true, data: null, error: false });
  const [category, setCategory] = useState(() => sessionStorage.getItem(FEE_KEY) || "unset");
  const sig = keys.join("|");
  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: false }));
    try {
      const { data } = await api.get("/realisations/compare-facts", { params: { keys: sig.split("|") }, paramsSerializer: { indexes: null } });
      setState({ loading: false, data, error: false });
    } catch {
      setState({ loading: false, data: null, error: true });
    }
  }, [sig]);
  useEffect(() => { load(); }, [load, nonce]);
  const pick = (c) => { setCategory(c); sessionStorage.setItem(FEE_KEY, c); };

  if (state.error) {
    return (
      <div role="alert" className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-rose-900" data-testid="compare-facts-error">
        We couldn't load the cost, fee and location details.{" "}
        <button type="button" onClick={load} className="rounded underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-700" data-testid="compare-facts-retry">Try again</button>
      </div>
    );
  }
  const d = state.data;
  if (!d) return <div role="status" className="mt-3 text-[12px] text-stone-500" data-testid="compare-facts-loading">Loading costs, fees and location…</div>;
  const byKey = Object.fromEntries((d.options || []).map((o) => [o.key, o]));
  const [a, b] = keys.map((k) => byKey[k]);
  if (!a || !b) return <div className="mt-3 text-[12px] text-stone-500" data-testid="compare-facts-unavailable">These choices are no longer on your shortlist.</div>;
  const opts = [a, b];
  const pref = d.study_region;
  const showDistance = d.home?.geo && !d.home.geo.approx;
  return (
    <div className="mt-3 space-y-2" data-testid="compare-facts" aria-busy={state.loading}>
      <Row title="Course" names={names} testId="compare-course" cells={opts.map((o, i) => <CourseCell o={o} i={i} />)} />
      <Row title="Living costs (estimate)" names={names} testId="compare-living" cells={opts.map((o, i) => <LivingCell o={o} budget={d.budget} i={i} />)}
        note={livingGap(a, b, format)} />
      <div className="min-w-0 border-t border-stone-200 pt-2" data-testid="compare-tuition">
        <div className="text-[11px] font-semibold text-stone-700">Tuition (per year, as listed)</div>
        <p className="text-[11px] text-stone-600" data-testid="compare-fee-status-note">
          We don't work out your fee status from nationality or where you live. Choose the category that applies to you, or check with each university.
        </p>
        <FeePicker value={category} onChange={pick} />
        <div className="mt-1 grid grid-cols-1 gap-2 md:grid-cols-2">
          {opts.map((o, i) => (
            <div key={i} className="min-w-0 break-words text-[12px] text-stone-700" data-testid={"compare-tuition-" + (i ? "b" : "a")}>
              <div className="text-[10px] font-underground-sc tracking-wider text-stone-500 md:hidden">{names[i]}</div>
              <TuitionCell o={o} category={category} i={i} />
            </div>
          ))}
        </div>
      </div>
      <Row title="Estimated yearly total" names={names} testId="compare-total" cells={opts.map((o, i) => <TotalCell o={o} category={category} i={i} />)} />
      <Row title="Where you'd study" names={names} testId="compare-region"
        note={pref ? null : "You haven't told us a preferred study region."}
        cells={opts.map((o, i) => {
          const r = regionLine(o, pref);
          return r ? <span className={TONE[r.tone]} data-testid={"compare-region-line-" + i}>{r.text}</span>
            : <span data-testid={"compare-region-line-" + i}>{o.region?.label ? "In " + o.region.label + "." : <Missing>Region not known for this city.</Missing>}</span>;
        })} />
      <Row title="Distance from home" names={names} testId="compare-distance" note={homeNote(d.home)}
        cells={opts.map((o, i) => (showDistance && o.distance_miles != null
          ? <span data-testid={"compare-distance-" + i}>Roughly {o.distance_miles} miles from {d.home.geo.label}</span>
          : <Missing testId={"compare-distance-missing-" + i}>{showDistance ? "No map location for this city." : "Not shown"}</Missing>))} />
    </div>
  );
}
